import { useCallback } from "react"
import { useMutation } from "urql"

import type { ResendToAllCustomersInput, ResendToAllCustomersMutation } from "@/graphql/codegen/graphql"
import { ResendToAllCustomersDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseResendToAllCustomersReturn {
  resendToAllCustomers: (input: ResendToAllCustomersInput) => Promise<boolean>
  isPromoting: boolean
}

export const useResendToAllCustomers = (): UseResendToAllCustomersReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    ResendToAllCustomersMutation,
    { input: ResendToAllCustomersInput }
  >(ResendToAllCustomersDocument)

  const resendToAllCustomers = useCallback(
    async (input: ResendToAllCustomersInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.resendToAllCustomers
      const hasError = error || output?.__typename === "ResendToAllCustomersFailure"

      if (hasError) {
        notify({ type: NotificationType.Error, message: "Unable to complete action. Please try again." })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes resent successfully." })
      return true
    },
    [executeMutation, notify]
  )

  return { resendToAllCustomers, isPromoting: fetching }
}
